<template>
  <div v-if="user" class="view-adminlicensecreate">
    <div class="breadcrump-trail">
      <router-link :to="{ name: 'admin-home' }"> Admin </router-link> /
      <router-link :to="{ name: 'admin-users-list' }"> Nutzer </router-link> /
      <router-link :to="{ name: 'admin-users-update', params: { userId } }">
        {{ userId }}
      </router-link>
      / Abo anlegen
    </div>

    <h1>Abo für {{ user.name }} ({{ user.email }}) erstellen</h1>

    <form
      class="column view-adminlicensecreate__form"
      @submit.prevent="createSubscription"
    >
      <div class="r-50-50">
        <input-dropdown
          v-model:value="plan"
          :options="orderablePlans"
          prop="label"
          label="Plan"
        />

        <input-dropdown
          v-model:value="data.paymentPeriod"
          :label="$t('labels.paymentPeriod')"
          :options="['year', 'semester', 'month']"
        >
          <template #default="{ item }">
            {{ $t(`periods.${item}`) }}
          </template>
        </input-dropdown>
      </div>

      <input-text
        v-if="plan?.subscriptionType == 'organization'"
        v-model:value="data.name"
        :label="$t('labels.organizationName')"
        :required="true"
        :error="fieldErrors?.name"
        autocomplete="new-organization-name"
      />

      <template v-if="plan?.canAddSeats">
        <hr />

        <div class="line_flex">
          <input-number
            :value="data.seats"
            :min="1"
            :label="$t('labels.licenseAmount')"
            :step="1"
            @update:value="data.seats = $event"
          />

          <input-number
            v-if="plan.canAddWorkspaces"
            :value="data.extraWorkspaces + 1"
            :label="$t('words.workspace', 2)"
            :min="1"
            :step="1"
            @update:value="data.extraWorkspaces = $event - 1"
          />
        </div>
      </template>

      <div v-if="plan?.upgradeable">
        <h4 class="label mt-2 mb-2">{{ $t("headings.upgrades") }}</h4>
        <div class="column">
          <div v-for="upgrade of subscriptionUpgrades">
            <input-checkbox
              :value="data.upgrades.includes(upgrade.id)"
              :label="$t(`upgrades.${upgrade.id}.name`)"
              @update:value="setUpgrade(upgrade.id, $event)"
            />
          </div>
        </div>
      </div>

      <hr />

      <input-checkbox
        :value="data.usesStandardPrices"
        class="mv-4"
        @update:value="data.usesStandardPrices = data.editable = data.invoiceAutomatically = $event"
      >
        <strong>Nutzt Standardpreise</strong>
        <div class="mt-1">
          Abstellen, falls Rabatte gewährt wurden oder Sonderkonditionen gelten!
        </div>
      </input-checkbox>

      <input-checkbox
        :value="data.editable"
        :disabled="!data.usesStandardPrices"
        class="mb-4"
        @update:value="data.editable = data.invoiceAutomatically = $event"
      >
        <strong>Vom Kunden änderbar</strong> <i v-if="!data.usesStandardPrices">(Nur mit Standardpreisen)</i>
        <div class="mt-1">
          Kunde kann selbständig verlängern und Nachbestellungen tätigen.
        </div>
      </input-checkbox>

      <input-checkbox
        :value="data.invoiceAutomatically"
        :disabled="!data.editable || !data.usesStandardPrices"
        class="mb-4"
        @update:value="data.invoiceAutomatically = $event"
      >
        <strong>Automatisch Rechnungen versenden</strong> <i v-if="!data.editable">(Nur wenn bearbeitbar)</i>
        <div class="mt-1">
          Erstellt und verschickt bei Verlängerung automatisch eine Rechnung aus LexOffice an die unten angegebene Adresse.
        </div>
        <div class="mt-1" v-show="!data.invoiceAutomatically && data.editable && data.usesStandardPrices">
          <strong>Bitte nur abwählen, wenn selbst Rechnungen verschickt werden. Nachbestellungen senden trotzdem Rechnungen!</strong>
        </div>
      </input-checkbox>

      <hr />

      <h4 class="mb-0 mt-1">{{ $t("headings.billingAddress") }}</h4>
      <input-text
        v-model:value="data.email"
        :label="$t('labels.email')"
        :required="true"
      />
      <address-form-fields
        v-model:firstname="data.firstname"
        v-model:lastname="data.lastname"
        v-model:company="data.company"
        v-model:vatId="data.vatId"
        v-model:street="data.street"
        v-model:supplement="data.supplement"
        v-model:zipcode="data.zipcode"
        v-model:city="data.city"
        v-model:country="data.country"
        :company-required="false"
        :show-form-errors="true"
      />

      <hr />

      <h4 class="mb-0 mt-1">Zeitraum</h4>

      <input-text
        v-model:value="data.validUntil"
        :label="$t('labels.validUntil')"
        type="date"
      />

      <input-checkbox
        v-model:value="data.renewAutomatically"
        :label="$t('labels.automaticRenewal')"
      />

      <hr />

      <button class="button mt-4" type="submit">Abo anlegen</button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { useAdmin } from "./useAdmin";

import InputDropdown from "@core/input/InputDropdown.vue";
import InputText from "@core/input/InputText.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import AddressFormFields from "@manager/common/AddressFormFields.vue";

import { usePlans, subscriptionUpgrades } from "@manager/plans/plans.store";
import { showError } from "@core/ui/alert/alert";
import { BadRequestError } from "@core/errors/Errors";

import type { AdminUser } from "@/api/User";
import type { LicensePlan } from "@manager/plans/plans.store";

const props = defineProps<{ userId?: string }>();

const router = useRouter();
const adminAPI = useAdmin();
const { plans, orderablePlans } = usePlans();

const user = ref<AdminUser | null>(null);
const plan = ref<LicensePlan | null>(null);
const data = reactive({
  paymentPeriod: "year" as "year" | "semester" | "month",
  seats: 1,
  extraWorkspaces: 0,
  upgrades: [] as string[],

  name: "",

  email: "",
  firstname: "",
  lastname: "",
  company: "",
  vatId: "",
  street: "",
  supplement: "",
  zipcode: "",
  city: "",
  country: "",

  validUntil: undefined as undefined | string,
  renewAutomatically: false,
  usesStandardPrices: true,
  editable: true,
  invoiceAutomatically: true
});

const fieldErrors = ref<{ [key: string]: string }>({});

loadUser();

async function loadUser() {
  const loadedUser = await adminAPI.get<AdminUser>(`/users/${props.userId}`);
  user.value = loadedUser;

  data.email = loadedUser.email;

  const subscription = loadedUser.subscriptions?.[0];

  console.log("subscription", subscription);

  if (subscription) {
    data.seats = subscription.seats || 0;

    data.paymentPeriod = subscription.paymentPeriod;
    plan.value = plans[subscription.plan as keyof typeof plans] || null;
    data.company = subscription.billingAddress?.company || "";
    data.vatId = subscription.billingAddress?.vatId || "";

    data.renewAutomatically = subscription.renewAutomatically == true;
    data.editable = subscription.editable == true;
    data.invoiceAutomatically = subscription.invoiceAutomatically == true;
  }
}

function setUpgrade(upgradeId: string, value: boolean) {
  if (value) {
    data.upgrades.push(upgradeId);
  } else {
    data.upgrades = data.upgrades.filter(id => id !== upgradeId);
  }
}

async function createSubscription() {
  try {
    if (plan.value == null) {
      throw new Error("Bitte Plan auswählen.");
    }

    if (data.paymentPeriod == null) {
      throw new Error("Bitte Zahlungsperiode auswählen.");
    }

    const subscriptionData: {
      plan: string;
      name: string;
      email: string;
      renewAutomatically: boolean;
      paymentPeriod: string;
      seats: number;
      upgrades: string[];

      billingAddress: {
        company?: string;
        vatId?: string;
        
        firstname: string;
        lastname: string;
  
        street: string;
        supplement: string;
        
        zipcode: string;
        city: string;
        country: string;
      },
      usesStandardPrices: boolean;
      editable: boolean;
      invoiceAutomatically: boolean;

      validUntil?: string;
    } = {
      plan: plan.value.id,
      renewAutomatically: data.renewAutomatically,

      name: data.name,

      email: data.email,
      paymentPeriod: data.paymentPeriod,

      seats: plan.value.canAddSeats ? data.seats : 1,
      upgrades: data.upgrades.slice(),
      
      billingAddress: {
        company: data.company,
        vatId: data.vatId,

        firstname: data.firstname,
        lastname: data.lastname,

        street: data.street,
        supplement: data.supplement,
        zipcode: data.zipcode,
        city: data.city,
        country: data.country
      },
      usesStandardPrices: data.usesStandardPrices,
      editable: data.editable && data.usesStandardPrices,
      invoiceAutomatically: data.invoiceAutomatically && data.usesStandardPrices
    };

    if (data.validUntil) {
      const validUntil = new Date(data.validUntil);
      subscriptionData.validUntil = validUntil.toISOString();
    }

    await adminAPI.post(`/users/${props.userId}/subscriptions`, { data: subscriptionData });

    router.push({
      name: "admin-users-update",
      params: { userId: props.userId }
    });
  } catch (_error) {
    const error = _error as Error | BadRequestError;
    fieldErrors.value = "fields" in error ? error?.fields : {};

    console.log(error);
    showError(error);
  }
}
</script>

<style lang="scss">
.view-adminlicensecreate {
  max-width: 75rem;
  width: 100%;
  padding: 2rem;

  &__form {
    max-width: 42rem;
  }
}
</style>
